import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import Input from '../ui/Input';
import Button from '../ui/Button';
import { useAuth } from '../../context/AuthContext';
import { trackEvent } from '../../utils/analytics';
import { LogIn } from 'lucide-react';

/** Connexion portail utilisateur — pas de lien admin public */
const Login: React.FC = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const location = useLocation();
    const { login } = useAuth();
    const redirectTo =
        typeof (location.state as { from?: string } | null)?.from === 'string'
            ? (location.state as { from: string }).from
            : '/espace';

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');
        try {
            const { data } = await api.post('/auth/login', { email, password });
            if (data.user?.role === 'admin') {
                setError('Compte administrateur : utilisez le portail dédié.');
                return;
            }
            login(data.user, data.token);
            trackEvent('login', { method: 'email' });
            navigate(redirectTo.startsWith('/') ? redirectTo : '/espace', { replace: true });
        } catch (err: any) {
            setError(err.response?.data?.error || err.message || 'Identifiants invalides');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center px-4 py-12 relative overflow-hidden">
            <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-indigo-200/20 blur-[120px] rounded-full" />
            <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-emerald-200/20 blur-[120px] rounded-full" />

            <div className="max-w-md w-full animate-fade-in">
                <div className="text-center mb-10">
                    <div className="inline-flex items-center justify-center p-2 bg-indigo-600 rounded-2xl shadow-xl shadow-indigo-200 mb-6">
                        <img src="/flashcv.png" alt="FlashCv Logo" className="w-10 h-10 object-contain" />
                    </div>
                    <h2 className="text-4xl font-extrabold text-slate-900 tracking-tight mb-2">Bon retour</h2>
                    <p className="text-slate-500 font-medium">Connectez-vous pour gérer vos CV</p>
                </div>

                <div className="card-premium p-8 sm:p-10 glass-morphism">
                    <form className="space-y-6" onSubmit={handleSubmit}>
                        <div className="space-y-4">
                            <Input
                                label="Email professionnel"
                                type="email"
                                placeholder="nom@exemple.com"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                            <Input
                                label="Mot de passe"
                                type="password"
                                placeholder="••••••••"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                            />
                        </div>

                        {error && (
                            <div className="p-3 bg-red-50 border border-red-100 rounded-xl text-red-600 text-sm text-center font-medium">
                                {error}
                            </div>
                        )}

                        <Button type="submit" className="w-full text-lg h-12" isLoading={isLoading}>
                            <LogIn className="w-5 h-5 mr-2" />
                            Se connecter
                        </Button>

                        <div className="pt-4 text-center border-t border-slate-100">
                            <p className="text-slate-500 text-sm">
                                Pas encore de compte ?{' '}
                                <button
                                    type="button"
                                    onClick={() =>
                                        navigate('/register', {
                                            state: { from: redirectTo !== '/espace' ? redirectTo : undefined },
                                        })
                                    }
                                    className="font-bold text-indigo-600 hover:text-indigo-700"
                                >
                                    S'inscrire gratuitement
                                </button>
                            </p>
                        </div>
                    </form>
                </div>

                <p className="mt-8 text-center text-slate-400 text-xs">&copy; 2026 FlashCV. Tout droits réservés.</p>
            </div>
        </div>
    );
};

export default Login;
